import React, { useCallback, useEffect, useState } from 'react';
import { AutocompleteSelect } from '@this/shared/ui/inputs/autocomplete';
import { Select } from '@this/shared/ui/inputs/select';
import { Fetcher } from '@this/src/util';
import BakurakuWorkflowRequest from '@this/domain/bakuraku_workflow_request';
import {
  buildBakurakuWorkflowRequestFromSnapshot,
  buildBeforeBakurakuWorkflowRequest,
  convertBakurakuWorkflowRequestResponseToArgs,
  formatBakurakuWorkflowRequestCaption,
  formatBakurakuWorkflowRequestLabel,
  type BakurakuWorkflowRequestResponseArgs
} from '@this/domain/bakuraku_workflow_request';
import type Trip from '@this/domain/trip/trip';
import type Traveler from '@this/domain/traveler/traveler';
import {
  MetaSection,
  MetaSectionFormActions,
  MetaSectionFormButton,
  MetaSectionTitle
} from '@this/components/organization/trips/detail/trip_detail.style';

type Props = {
  trip: Trip;
  bakurakuWorkflowIntegrationEnabled: boolean;
  onSave: (request: BakurakuWorkflowRequest | null) => void;
};

type BakurakuWorkflowRequestsResponse = {
  bakuraku_workflow_requests: BakurakuWorkflowRequestResponseArgs[];
};

const buildCurrentRequest = (trip: Trip) =>
  buildBakurakuWorkflowRequestFromSnapshot({
    bakurakuWorkflowRequestId: trip.bakurakuWorkflowRequestId,
    bakurakuWorkflowRequestTitle: trip.bakurakuWorkflowRequestTitle,
    bakurakuWorkflowRequestNum: trip.bakurakuWorkflowRequestNum
  });

const BakurakuWorkflowRequestForm: React.FC<Props> = ({ trip, bakurakuWorkflowIntegrationEnabled, onSave }) => {
  const [selectedRequest, setSelectedRequest] = useState<BakurakuWorkflowRequest | null>(
    buildCurrentRequest(trip)
  );
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [statusOptions, setStatusOptions] = useState<string[]>([]);
  const [actualCount, setActualCount] = useState(0);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [selectorKey, setSelectorKey] = useState(0);

  useEffect(() => {
    setSelectedRequest(buildCurrentRequest(trip));
  }, [trip.bakurakuWorkflowRequestId, trip.bakurakuWorkflowRequestTitle, trip.bakurakuWorkflowRequestNum]);

  const currentRequest = buildCurrentRequest(trip);
  const selectedRequestLabel = formatBakurakuWorkflowRequestLabel(selectedRequest);
  const selectedRequestCaption = formatBakurakuWorkflowRequestCaption(selectedRequest);
  const isChanged =
    (currentRequest === null && selectedRequest !== null) ||
    (currentRequest !== null && selectedRequest === null) ||
    (currentRequest !== null && selectedRequest !== null && !selectedRequest.matches(currentRequest));

  const loadBakurakuWorkflowRequests = useCallback(
    async (keyword?: string) => {
      setLoading(true);
      setFetchError(null);

      try {
        const travelerEmails = Array.from(
          new Set(
            [trip.user?.email, ...trip.travelers.list.map((traveler: Traveler) => traveler.email)].filter(
              Boolean
            ) as string[]
          )
        );
        const result = await Fetcher.get<BakurakuWorkflowRequestsResponse>(
          '/organization/trips/bakuraku_workflow_requests',
          {
            trip_id: trip.id,
            keyword,
            status,
            traveler_emails: travelerEmails,
            request_date: Date.now()
          }
        );
        const requests = result.bakuraku_workflow_requests.map(
          request => new BakurakuWorkflowRequest(convertBakurakuWorkflowRequestResponseToArgs(request))
        );
        const nextStatusOptions = Array.from(
          new Set(requests.map(request => request.status).filter(Boolean) as string[])
        );

        setStatusOptions(
          status && !nextStatusOptions.includes(status) ? [status, ...nextStatusOptions] : nextStatusOptions
        );
        setActualCount(requests.length);
        setLoading(false);

        return [buildBeforeBakurakuWorkflowRequest(), ...requests];
      } catch {
        setLoading(false);
        setActualCount(0);
        setFetchError('Bakuraku申請の取得に失敗しました。');
        return [buildBeforeBakurakuWorkflowRequest()];
      }
    },
    [status, trip]
  );

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatus(e.target.value);
    setSelectorKey(prev => prev + 1);
  };

  useEffect(() => {
    if (!bakurakuWorkflowIntegrationEnabled) {
      return;
    }

    void loadBakurakuWorkflowRequests();
  }, [bakurakuWorkflowIntegrationEnabled, loadBakurakuWorkflowRequests]);

  if (!bakurakuWorkflowIntegrationEnabled) {
    return null;
  }

  return (
    <MetaSection>
      <MetaSectionTitle>Bakuraku事前申請</MetaSectionTitle>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ maxWidth: '180px' }}>
          <Select value={status} onChange={handleStatusChange} fullWidth>
            <option value="">すべてのステータス</option>
            {statusOptions.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        </div>
        <AutocompleteSelect<BakurakuWorkflowRequest>
          key={selectorKey}
          async="FILTERING"
          inputChangeDelay={200}
          getOptions={loadBakurakuWorkflowRequests}
          value={selectedRequest}
          getOptionLabel={option => option.label}
          getOptionCaption={option => option.caption}
          getOptionSelected={(option, value) => option.matches(value)}
          onChange={(_event, request) => setSelectedRequest(request)}
          placeholder="Bakuraku申請を検索"
          fullWidth
        />
        {loading && <p style={{ margin: 0, color: '#666', fontSize: '12px' }}>Bakuraku申請を読み込んでいます。</p>}
        {selectedRequestLabel && <p style={{ margin: 0, color: '#222' }}>{selectedRequestLabel}</p>}
        {selectedRequestCaption && selectedRequestCaption !== selectedRequestLabel && (
          <p style={{ margin: 0, color: '#666', fontSize: '12px' }}>{selectedRequestCaption}</p>
        )}
        {fetchError && <p style={{ margin: 0, color: '#bf2600', fontSize: '12px' }}>{fetchError}</p>}
        {!loading && actualCount === 0 && !fetchError && (
          <p style={{ margin: 0, color: '#666', fontSize: '12px' }}>一致するBakuraku申請はありません。</p>
        )}
      </div>

      {isChanged && (
        <MetaSectionFormActions>
          <MetaSectionFormButton onClick={() => onSave(selectedRequest)}>保存</MetaSectionFormButton>
          <MetaSectionFormButton onClick={() => setSelectedRequest(currentRequest)}>
            キャンセル
          </MetaSectionFormButton>
        </MetaSectionFormActions>
      )}
    </MetaSection>
  );
};

export default BakurakuWorkflowRequestForm;
